import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { pushNotification } from '@/lib/notifications';

// ─────────────────────────────────────────────────────────────
// paymentAttempts — journal des tentatives de paiement échouées.
//
// Chaque refus d'un fournisseur (FeexPay en premier) est enregistré avec
// le montant demandé, le montant réellement envoyé à la passerelle, la
// devise, l'opérateur, le numéro (masqué), le statut HTTP, l'endpoint et
// la réponse BRUTE du fournisseur. Objectif : diagnostiquer un échec sans
// avoir à reproduire le paiement.
//
// L'écriture est toujours « best-effort » : elle ne doit jamais casser le
// tunnel de paiement.
// ─────────────────────────────────────────────────────────────

export type PaymentFlow = 'subscription' | 'shop_order' | 'other';
export type PaymentAttemptStatus = 'failed' | 'declined' | 'error';

export interface PaymentAttemptInput {
  provider?: string;
  flow?: PaymentFlow;
  status?: PaymentAttemptStatus;
  amount?: number | null;
  currency?: string | null;
  gatewayAmount?: number | null;
  gatewayCurrency?: string | null;
  operator?: string | null;
  country?: string | null;
  phone?: string | null;
  httpStatus?: number | null;
  endpoint?: string | null;
  reference?: string | null;
  subscriptionId?: number | null;
  orderId?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  rawResponse?: unknown;
  requestPayload?: unknown;
}

export interface PaymentAttemptRow {
  id: number;
  provider: string;
  flow: string | null;
  status: string;
  amount: number | null;
  currency: string | null;
  gateway_amount: number | null;
  gateway_currency: string | null;
  operator: string | null;
  country: string | null;
  phone: string | null;
  http_status: number | null;
  endpoint: string | null;
  reference: string | null;
  subscription_id: number | null;
  order_id: number | null;
  error_code: string | null;
  error_message: string | null;
  raw_response: string | null;
  request_payload: Record<string, unknown> | null;
  user_id: string | null;
  user_label: string | null;
  created_at: string;
}

const MAX_TEXT = 4000;

/** Masque un numéro de téléphone (on garde les 3 derniers chiffres). */
export function maskPhone(raw: unknown): string | null {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length <= 3) return digits;
  return `${'•'.repeat(Math.min(digits.length - 3, 9))}${digits.slice(-3)}`;
}

function toText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.slice(0, MAX_TEXT);
  try {
    return JSON.stringify(value).slice(0, MAX_TEXT);
  } catch {
    return String(value).slice(0, MAX_TEXT);
  }
}

function toJson(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return { value: toText(value) };
}

/** Motifs qui traduisent un manque de fonds / plafond atteint chez l'opérateur. */
const INSUFFICIENT_FUNDS_RE =
  /LOW_BALANCE|INSUFFICIENT|LIMIT_REACHED|NOT_ALLOWED|FONDS INSUFFISANTS|SOLDE INSUFFISANT|PLAFOND/i;

/**
 * 🔔 Alerte métier : quand un marchand essuie 2 refus « fonds insuffisants »
 * (ou plus) en 24 h, on prévient le marchand ET les SuperAdmins pour qu'ils
 * puissent réagir avant que l'abonnement ne soit bloqué.
 *
 * Best-effort, jamais bloquant, et limité en volume (une alerte à 2 refus,
 * puis une tous les 5 refus) pour ne pas spammer la cloche.
 */
async function alertRepeatedInsufficientFunds(
  input: PaymentAttemptInput,
  authUserId: string | null,
  authEmail: string | null,
): Promise<void> {
  const signal = `${input.errorCode || ''} ${input.errorMessage || ''}`;
  if (!INSUFFICIENT_FUNDS_RE.test(signal)) return;
  if (!authUserId) return;

  const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from('payment_attempt_logs')
    .select('id, error_code, error_message')
    .eq('user_id', authUserId)
    .gte('created_at', since)
    .limit(100);
  if (error || !data) return;

  const similar = (data as { error_code: string | null; error_message: string | null }[]).filter((row) =>
    INSUFFICIENT_FUNDS_RE.test(`${row.error_code || ''} ${row.error_message || ''}`),
  );
  const occurrences = similar.length;
  if (occurrences < 2) return;
  // Une alerte à 2 refus, puis tous les 5 refus : pas de spam.
  if (occurrences !== 2 && occurrences % 5 !== 0) return;

  const montant = Number(input.amount) || 0;
  const devise = input.currency || '';
  const operateur = input.operator || 'Mobile Money';
  const parts = [
    `${occurrences} paiement${occurrences > 1 ? 's' : ''} refusé${occurrences > 1 ? 's' : ''} en 24 h — fonds insuffisants`, 
    operateur,
    montant ? `${montant.toLocaleString('fr-FR')} ${devise}`.trim() : '',
  ].filter(Boolean);
  const message = `${parts.join(' · ')}. Rechargez votre compte Mobile Money puis relancez le paiement depuis Mon abonnement.`;

  // 1) Le marchand concerné (correspondance email → id numérique).
  if (authEmail) {
    try {
      const { data: merchant } = await supabase
        .from('users')
        .select('id')
        .eq('email', authEmail)
        .maybeSingle();
      const merchantId = Number((merchant as { id?: number } | null)?.id);
      if (merchantId > 0) {
        await pushNotification({
          userId: merchantId,
          type: 'payment_failed',
          message,
          publicationId: Number(input.subscriptionId) || 0,
        });
      }
    } catch {
      /* best-effort */
    }
  }

  // 2) Les SuperAdmins (typecompte 0 et 1).
  try {
    const { data: admins } = await supabase
      .from('users')
      .select('id')
      .in('typecompte', [0, 1])
      .limit(5);
    for (const admin of (admins || []) as { id: number }[]) {
      await pushNotification({
        userId: Number(admin.id),
        type: 'payment_failed',
        message: `Marchand ${authEmail || ''} : ${message}`.slice(0, 240),
        publicationId: Number(input.subscriptionId) || 0,
      });
    }
  } catch {
    /* best-effort */
  }
}

export async function logPaymentAttempt(input: PaymentAttemptInput): Promise<void> {
  try {
    let userId: string | null = null;
    let userLabel: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      const authUser = data.session?.user;
      if (authUser) {
        userId = authUser.id;
        userLabel = authUser.email || null;
      }
    } catch {
      /* session indisponible : on journalise quand même */
    }

    const { data: insertedRow } = await supabase.from('payment_attempt_logs').insert({
      provider: (input.provider || 'feexpay').toLowerCase(),
      flow: input.flow || 'other',
      status: input.status || 'failed',
      amount: Number.isFinite(Number(input.amount)) ? Number(input.amount) : null,
      currency: input.currency ? String(input.currency).toUpperCase() : null,
      gateway_amount: Number.isFinite(Number(input.gatewayAmount)) ? Number(input.gatewayAmount) : null,
      gateway_currency: input.gatewayCurrency ? String(input.gatewayCurrency).toUpperCase() : null,
      operator: input.operator || null,
      country: input.country || null,
      phone: maskPhone(input.phone),
      http_status: Number.isFinite(Number(input.httpStatus)) ? Number(input.httpStatus) : null,
      endpoint: input.endpoint || null,
      reference: input.reference || null,
      subscription_id: Number(input.subscriptionId) > 0 ? Number(input.subscriptionId) : null,
      order_id: Number(input.orderId) > 0 ? Number(input.orderId) : null,
      error_code: input.errorCode || null,
      error_message: (input.errorMessage || 'Échec du paiement').slice(0, 1200),
      raw_response: toText(input.rawResponse),
      request_payload: toJson(input.requestPayload),
      user_id: userId,
      user_label: userLabel,
      created_at: new Date().toISOString(),
    }).select('id').maybeSingle();

    // 🔔 Pastille SuperAdmin : signale l'échec immédiatement dans le back-office
    // (sans attendre le prochain rafraîchissement du journal).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('zifek:payment-failed', {
        detail: {
          id: Number(insertedRow?.id) || null,
          provider: (input.provider || 'feexpay').toLowerCase(),
          message: input.errorMessage || 'Échec du paiement',
        },
      }));
    }

    // Double entrée : le journal système expose aussi l'échec en clair.
    logger.warn('paiement', `${input.provider || 'feexpay'} — ${input.errorMessage || 'échec du paiement'}`, {
      montant: input.amount ?? null,
      devise: input.currency || null,
      montant_envoye: input.gatewayAmount ?? null,
      operateur: input.operator || null,
      pays: input.country || null,
      statut_http: input.httpStatus ?? null,
      endpoint: input.endpoint || null,
      reference: input.reference || null,
    });

    // 🔔 Refus « fonds insuffisants » répétés : alerte marchand + SuperAdmin.
    await alertRepeatedInsufficientFunds(input, userId, userLabel);
  } catch {
    /* le journal ne doit jamais faire tomber le tunnel de paiement */
  }
}

/** Nombre de tentatives échouées sur une fenêtre (pour l'indicateur du journal). */
export async function countFailedAttempts(hours = 24): Promise<number> {
  const since = new Date(Date.now() - hours * 3600 * 1000).toISOString();
  const { count } = await supabase
    .from('payment_attempt_logs')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', since);
  return count || 0;
}